import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type AuditEntry,
  type BreakerEntry,
  type QuotaEntry,
  type SchedulerJobStatus,
} from "@/lib/api";
import { RedetectButton } from "@/routes/Exchanges";
import { fmtQuota, fmtTimestamp, fmtUntilNext } from "@/lib/format";
import { SectionTabs, SYSTEM_TABS } from "@/components/SectionTabs";
import { Badge, type BadgeProps, lifecycleVariant, taskStatusVariant, decisionVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";

// ---------------------------------------------------------------------------
// System Status — Day 23. Snapshot view aggregating channel/task/sponsorship/
// campaign counts, per-platform quota, and the last 50 audit log entries.
// ---------------------------------------------------------------------------

// v2.13.x — jobs whose manual "立刻跑" is irreversible / costly: LLM-calling
// (Gemini quota + $), Telegram-posting (lands in the operator chat, no undo),
// or full-table-scan + rewrite. The button confirms before firing these.
const EXPENSIVE_JOBS = new Set<string>([
  "compute_scores",          // full-table scan + rewrites channel_scores
  "llm_audit_sponsorships",  // ~100 Gemini calls
  "llm_review_pending",      // LLM calls
  "exchange_insights_prewarm", // LLM calls
  "daily_report",            // posts to Telegram
  "weekly_report",           // posts to Telegram
]);

// QueryError renders a fetch failure so a degraded backend isn't silently
// indistinguishable from "loading" or "empty" (v2.13.x — every section's
// query previously rendered a 500 as if it were still loading / had no data).
function QueryError({ q }: { q: { isError: boolean; error: unknown } }) {
  if (!q.isError) return null;
  return (
    <p className="text-xs text-destructive">
      加载失败：{(q.error as Error)?.message ?? "未知错误"}
    </p>
  );
}

export default function System() {
  const statsQ = useQuery({
    queryKey: ["system-stats"],
    queryFn: () => endpoints.systemStats(),
    refetchInterval: 30_000,
  });
  const quotaQ = useQuery({
    queryKey: ["system-quota"],
    queryFn: () => endpoints.systemQuota(),
    refetchInterval: 60_000,
  });
  const auditQ = useQuery({
    queryKey: ["audit-recent"],
    queryFn: () => endpoints.auditRecent(50),
    refetchInterval: 30_000,
  });
  const jobsQ = useQuery({
    queryKey: ["scheduler-jobs"],
    queryFn: () => endpoints.schedulerJobs(),
    refetchInterval: 15_000,
  });
  const healthQ = useQuery({
    queryKey: ["health"],
    queryFn: () => endpoints.health(),
  });
  const breakersQ = useQuery({
    queryKey: ["system-breakers"],
    queryFn: () => endpoints.systemBreakers(),
    refetchInterval: 15_000,
  });

  const stats = statsQ.data;
  const breakers = breakersQ.data?.breakers ?? [];

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <SectionTabs tabs={SYSTEM_TABS} />
      <div className="border-b border-border bg-background px-6 py-3">
        <h1 className="text-lg font-semibold">系统状态</h1>
        <p className="text-xs text-muted-foreground">每 30 秒自动刷新</p>
      </div>

      <div className="mx-auto max-w-6xl space-y-5 p-6">
        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">服务健康</h2>
          {healthQ.data ? (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat label="服务状态" value={healthQ.data.status} accent={healthQ.data.status === "ok" ? "text-emerald-600" : "text-amber-600"} />
              <Stat label="数据库" value={healthQ.data.database} />
              <Stat label="版本" value={healthQ.data.version} />
            </div>
          ) : healthQ.isError ? (
            <QueryError q={healthQ} />
          ) : (
            <p className="text-xs text-muted-foreground">加载中…</p>
          )}
        </section>

        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">配额状态（按平台）</h2>
          {quotaQ.data?.quotas && quotaQ.data.quotas.length > 0 ? (
            <div className="grid gap-2 md:grid-cols-3">
              {quotaQ.data.quotas.map((q) => (
                <QuotaCard key={q.platform} q={q} />
              ))}
            </div>
          ) : quotaQ.isError ? (
            <QueryError q={quotaQ} />
          ) : quotaQ.isLoading ? (
            <p className="text-xs text-muted-foreground">加载中…</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              暂无配额记录（quota_status_check 任务尚未跑过）
            </p>
          )}
        </section>

        {(breakers.length > 0 || breakersQ.isError) && (
          <section className="rounded-md border border-border bg-background p-4">
            <h2 className="mb-1 text-sm font-semibold">依赖熔断器</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              外部 sidecar 连续失败时自动熔断，快速失败而非空等超时；冷却后自动半开探活。
              “熔断”≠“依赖彻底死亡”。
            </p>
            {breakersQ.isError ? (
              <QueryError q={breakersQ} />
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {breakers.map((b) => (
                  <BreakerCard key={b.name} b={b} />
                ))}
              </div>
            )}
          </section>
        )}

        {stats ? (
          <div className="grid gap-4 md:grid-cols-2">
            <BucketCard
              title="频道生命周期"
              buckets={stats.channels_by_lifecycle}
              variantFor={lifecycleVariant}
            />
            <BucketCard
              title="评估任务"
              buckets={stats.tasks_by_status}
              variantFor={taskStatusVariant}
            />
            <BucketCard
              title="推广审核"
              buckets={stats.sponsorships_by_review}
              variantFor={decisionVariant}
            />
            <BucketCard
              title="投放"
              buckets={stats.campaigns_by_status}
              variantFor={decisionVariant}
            />
          </div>
        ) : statsQ.isError ? (
          // v2.13.x — was `{stats && ...}`, so a failed /system/stats just
          // vanished the 4 bucket cards as if there were no data. Surface it.
          <section className="rounded-md border border-border bg-background p-4">
            <QueryError q={statsQ} />
          </section>
        ) : null}

        <section className="rounded-md border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold">调度器状态</h2>
              <p className="text-xs text-muted-foreground">
                cron 自动跑；每条后面"立刻跑"按钮可手动触发（每 15 秒刷新）·
                channel_scan_videos 支持 lookback 参数 prompt
              </p>
            </div>
            <RedetectButton />
          </div>
          {jobsQ.isLoading && (
            <p className="p-4 text-sm text-muted-foreground">加载中…</p>
          )}
          {jobsQ.data && jobsQ.data.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              没有注册的任务（可能 --no-scheduler 启动了，或 DB 未连接）
            </p>
          )}
          {jobsQ.data && jobsQ.data.length > 0 && (
            <SchedulerTable jobs={jobsQ.data} onRefetch={() => jobsQ.refetch()} />
          )}
        </section>

        <section className="rounded-md border border-border bg-background">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">最近审计日志</h2>
            <p className="text-xs text-muted-foreground">
              {auditQ.data?.data.length ?? 0} 条
            </p>
          </div>
          {auditQ.isLoading && (
            <p className="p-4 text-sm text-muted-foreground">加载中…</p>
          )}
          {auditQ.data && auditQ.data.data.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">暂无审计记录</p>
          )}
          {auditQ.data && auditQ.data.data.length > 0 && (
            <AuditTable entries={auditQ.data.data} />
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold ${accent ?? ""}`}>{value}</div>
    </div>
  );
}

function QuotaCard({ q }: { q: QuotaEntry }) {
  const unlimited = q.remaining >= 2147483647;
  const low = !unlimited && q.remaining < 1000;
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{q.platform}</span>
        {low && <Badge variant="warning">低</Badge>}
      </div>
      <div className="text-xl font-semibold">{fmtQuota(q.remaining)}</div>
      <div className="text-[10px] text-muted-foreground">
        检查于 {new Date(q.checked_at).toLocaleString()}
      </div>
    </div>
  );
}

// BreakerCard renders one circuit breaker's state. Green = healthy passthrough,
// red = tripped (failing fast), amber = probing recovery.
function BreakerCard({ b }: { b: BreakerEntry }) {
  const meta: Record<BreakerEntry["state"], { label: string; variant: BadgeProps["variant"]; accent: string }> = {
    closed: { label: "正常", variant: "success", accent: "text-emerald-600" },
    open: { label: "已熔断", variant: "danger", accent: "text-rose-600" },
    "half-open": { label: "探活中", variant: "warning", accent: "text-amber-600" },
  };
  const m = meta[b.state];
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs text-muted-foreground">{b.name}</span>
        <Badge variant={m.variant}>{m.label}</Badge>
      </div>
      <div className={`text-sm font-semibold ${m.accent}`}>
        {b.state === "open"
          ? `快速失败中${b.cooldown_remaining_sec ? `，${b.cooldown_remaining_sec}s 后探活` : "（即将探活）"}`
          : b.state === "half-open"
            ? "正在用一次探活请求确认依赖是否恢复"
            : `连续失败 ${b.consec_failures} 次（阈值前重置）`}
      </div>
      <div className="text-[10px] text-muted-foreground">
        累计熔断 {b.trips} 次
        {b.last_transition
          ? ` · 最近状态变化 ${new Date(b.last_transition).toLocaleString()}`
          : ""}
      </div>
    </div>
  );
}

function BucketCard({
  title,
  buckets,
  variantFor,
}: {
  title: string;
  buckets: Record<string, number>;
  variantFor: (s: string) => BadgeProps["variant"];
}) {
  const entries = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  return (
    <section className="rounded-md border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs text-muted-foreground">总 {total}</span>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([bucket, n]) => {
            const pct = total > 0 ? (n / total) * 100 : 0;
            return (
              <li key={bucket}>
                <div className="mb-1 flex items-center gap-2 text-xs">
                  <Badge variant={variantFor(bucket) ?? "secondary"}>{bucket}</Badge>
                  <span className="ml-auto font-medium">{n}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted">
                  <div
                    className="h-1.5 rounded-full bg-primary"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function AuditTable({ entries }: { entries: AuditEntry[] }) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>时间</TH>
          <TH>操作者</TH>
          <TH>动作</TH>
          <TH>实体</TH>
          <TH>详情</TH>
        </TR>
      </THead>
      <TBody>
        {entries.map((e) => {
          // v2.2.4 — backend now returns Details as raw JSON (was
          // base64-encoded string before); decodeJSONB is no longer
          // needed. Falsy/null check below covers historical "{}" and
          // null cases.
          const details = e.Details;
          const hasDetails = details !== null && details !== undefined &&
            !(typeof details === "object" && Object.keys(details as object).length === 0);
          return (
            <TR key={e.ID}>
              <TD className="whitespace-nowrap text-xs text-muted-foreground">
                {fmtTimestamp(e.Timestamp)}
              </TD>
              <TD className="text-xs">{e.Actor ?? "—"}</TD>
              <TD className="font-mono text-xs">{e.Action}</TD>
              <TD className="text-xs">
                {e.EntityType ?? "—"}
                {e.EntityID ? ` #${e.EntityID}` : ""}
              </TD>
              <TD className="max-w-md truncate text-xs text-muted-foreground">
                {hasDetails ? JSON.stringify(details) : "—"}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

// SchedulerTable renders the per-job last-run + next-run snapshot with an
// inline "立刻跑" trigger. Pending mutations show a spinner button so the
// operator knows the click registered; refetch after success/failure keeps
// the table in sync with the server.
function SchedulerTable({
  jobs,
  onRefetch,
}: {
  jobs: SchedulerJobStatus[];
  onRefetch: () => void;
}) {
  const qc = useQueryClient();
  // v1.4.7: jobName -> optional lookback override for per-invocation params.
  // Currently only channel_scan_videos consumes lookback_days.
  const run = useMutation({
    mutationFn: (args: { name: string; lookbackDays?: number }) =>
      endpoints.runSchedulerJob(args.name, { lookbackDays: args.lookbackDays }),
    // v2.13.x — surface failures immediately. Without onError a bad job name,
    // a down scheduler, or a validation error was silent for ~15s until the
    // next refetch. Mirrors Exchanges.tsx RedetectButton.
    onError: (err) => alert("运行失败: " + (err as Error).message),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["scheduler-jobs"] });
      qc.invalidateQueries({ queryKey: ["audit-recent"] });
      onRefetch();
    },
  });

  // v2.4.5 — group by stage. 17+ jobs in one flat table is hard to scan;
  // grouped by semantic bucket (1.数据采集 / 2.统计更新 / 3.推广+评估 /
  // 4.LLM / 5.报告+告警 / 6.维护) reads like an outline. Each group has
  // a header with rollup stats so the operator can see "anything failing?
  // running?" without scanning every row.
  const groups = new Map<number, { label: string; jobs: SchedulerJobStatus[] }>();
  for (const j of jobs) {
    const g = groups.get(j.stage) ?? { label: j.stage_label ?? `阶段 ${j.stage}`, jobs: [] };
    g.jobs.push(j);
    groups.set(j.stage, g);
  }
  // Sort within each group by name; group itself iterated in stage order.
  for (const g of groups.values()) {
    g.jobs.sort((a, b) => a.name.localeCompare(b.name));
  }
  const sortedGroups = [...groups.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <div className="space-y-6">
      {sortedGroups.map(([stage, group]) => {
        const failed = group.jobs.filter((j) => j.last_status === "failed").length;
        const running = group.jobs.filter((j) => j.is_running).length;
        const neverRan = group.jobs.filter((j) => !j.last_status).length;
        return (
          <section key={stage}>
            <div className="mb-2 flex items-baseline justify-between border-b border-border pb-1">
              <h3 className="text-sm font-semibold">{group.label}</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {group.jobs.length} 任务
                {failed > 0 && (
                  <Badge variant="danger" className="text-[10px]">
                    {failed} 失败
                  </Badge>
                )}
                {running > 0 && (
                  <Badge variant="info" className="text-[10px]">
                    {running} 运行中
                  </Badge>
                )}
                {neverRan > 0 && failed === 0 && running === 0 && (
                  <span className="text-[10px]">{neverRan} 未跑过</span>
                )}
              </div>
            </div>
            <SchedulerGroupTable jobs={group.jobs} run={run} />
          </section>
        );
      })}
    </div>
  );
}

// SchedulerGroupTable renders one stage's jobs as a table; pulled out so
// the outer component stays focused on grouping + per-group rollup.
function SchedulerGroupTable({
  jobs,
  run,
}: {
  jobs: SchedulerJobStatus[];
  run: ReturnType<typeof useMutation<unknown, Error, { name: string; lookbackDays?: number }>>;
}) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>任务</TH>
          <TH>cron</TH>
          <TH>上次结果</TH>
          <TH>上次跑</TH>
          <TH>耗时</TH>
          <TH>下次</TH>
          <TH className="text-right">动作</TH>
        </TR>
      </THead>
      <TBody>
        {jobs.map((j) => (
          <TR key={j.name}>
            <TD>
              <div className="font-mono text-xs">{j.name}</div>
              {j.description && (
                <div className="mt-0.5 max-w-[28rem] text-xs leading-snug text-muted-foreground">
                  {j.description}
                </div>
              )}
              <div className="flex gap-1 pt-1">
                {j.optional && <Badge variant="secondary">optional</Badge>}
                {!j.enabled && <Badge variant="outline">disabled</Badge>}
                {j.is_running && <Badge variant="info">running</Badge>}
              </div>
            </TD>
            <TD className="font-mono text-xs text-muted-foreground">
              {j.schedule}
            </TD>
            <TD>
              {j.last_status === "ok" && <Badge variant="success">ok</Badge>}
              {j.last_status === "failed" && <Badge variant="danger">failed</Badge>}
              {!j.last_status && (
                <span className="text-xs text-muted-foreground">未跑过</span>
              )}
              {j.last_error && (
                <div className="mt-0.5 max-w-[20rem] truncate text-[10px] text-destructive">
                  {j.last_error}
                </div>
              )}
            </TD>
            <TD className="whitespace-nowrap text-xs text-muted-foreground">
              {fmtTimestamp(j.last_run_at)}
            </TD>
            <TD className="text-xs text-muted-foreground">
              {j.last_duration_ms !== undefined && j.last_duration_ms > 0
                ? `${j.last_duration_ms} ms`
                : "—"}
            </TD>
            <TD className="whitespace-nowrap text-xs text-muted-foreground">
              {/* v2.4.3 — show relative "in X" alongside absolute next_run_at.
                  Operator no longer has to mental-math "次 21:00,现在 19:30,
                  还有 1.5 小时". */}
              {fmtTimestamp(j.next_run_at)}
              {j.next_run_at && (
                <div className="text-[10px] text-muted-foreground/70">
                  {fmtUntilNext(j.next_run_at as unknown as string)}
                </div>
              )}
            </TD>
            <TD className="text-right">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  // v2.13.x — confirm before firing an EXPENSIVE job. These
                  // either cost real money (LLM calls), post to the operator's
                  // Telegram (no undo), or full-scan + rewrite a table. A
                  // misclick was previously irreversible + silent.
                  if (EXPENSIVE_JOBS.has(j.name)) {
                    if (!confirm(`「${j.name}」是高成本任务（LLM 调用 / 发 Telegram / 全表扫描），确认立刻运行？`)) {
                      return;
                    }
                  }
                  // channel_scan_videos accepts an optional lookback override
                  // for backfilling history. Other jobs run with default params.
                  if (j.name === "channel_scan_videos") {
                    const input = prompt(
                      "Lookback 天数（默认 90，最大 730）— 留空使用默认：",
                      "90",
                    );
                    if (input === null) return; // cancelled
                    const days = input.trim() === "" ? 0 : Number(input);
                    if (Number.isNaN(days) || days < 0 || days > 730) {
                      alert("请输入 0-730 的整数");
                      return;
                    }
                    run.mutate({ name: j.name, lookbackDays: days || undefined });
                    return;
                  }
                  run.mutate({ name: j.name });
                }}
                disabled={
                  !j.enabled ||
                  j.is_running ||
                  (run.isPending && run.variables?.name === j.name)
                }
              >
                {run.isPending && run.variables?.name === j.name
                  ? "运行中…"
                  : "立刻跑"}
              </Button>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
